<template>
  <div class="status-bar" :class="{ 'status-bar-offline': !isOnline }">
    <div v-if="isOnline" class="status-content">
      <!-- Settings Icon -->
      <div class="status-item status-item-clickable" @click.stop="openSettingsDialog">
        <i class="pi pi-cog status-icon mr-1" style="color: var(--text-color-secondary)"></i>
        <span class="status-label">Settings</span>
      </div>

      <div class="status-divider"></div>

      <!-- Scale Icon -->
      <div class="status-item">
        <i
          class="pi pi-gauge status-icon mr-1 status-icon-clickable"
          :style="{ color: getDeviceIconColor(defaultScale?.id) }"
          @click.stop="openDeviceDetails('scale', defaultScale?.id)"
        ></i>
        <span class="status-label">Scale:</span>
        <span v-if="defaultScale" class="status-value">
          {{ defaultScale.name }}
          <Tag
            :value="getDeviceStatus(defaultScale.id)"
            :severity="getDeviceStatusSeverity(defaultScale.id)"
            class="ml-1"
          />
        </span>
        <span v-else class="status-value text-500">Not Selected</span>
      </div>

      <div class="status-divider"></div>

      <!-- Camera Icon -->
      <div class="status-item">
        <i
          class="pi pi-camera status-icon mr-1 status-icon-clickable"
          :style="{ color: getDeviceIconColor(defaultCamera?.id) }"
          @click.stop="openDeviceDetails('camera', defaultCamera?.id)"
        ></i>
        <span class="status-label">Camera:</span>
        <span v-if="defaultCamera" class="status-value">
          {{ defaultCamera.name }}
          <Tag
            :value="getDeviceStatus(defaultCamera.id)"
            :severity="getDeviceStatusSeverity(defaultCamera.id)"
            class="ml-1"
          />
        </span>
        <span v-else class="status-value text-500">Not Selected</span>
      </div>

      <div class="status-divider"></div>

      <!-- Scanner Icon -->
      <div class="status-item">
        <i
          class="pi pi-qrcode status-icon mr-1 status-icon-clickable"
          :style="{ color: getDeviceIconColor(defaultScanner?.id) }"
          @click.stop="openDeviceDetails('scanner', defaultScanner?.id)"
        ></i>
        <span class="status-label">Scanner:</span>
        <span v-if="defaultScanner" class="status-value">
          {{ defaultScanner.name }}
          <Tag
            :value="getDeviceStatus(defaultScanner.id)"
            :severity="getDeviceStatusSeverity(defaultScanner.id)"
            class="ml-1"
          />
        </span>
        <span v-else class="status-value text-500">Not Selected</span>
      </div>

      <div class="status-divider"></div>

      <!-- Version Icon -->
      <div class="status-item">
        <i
          class="pi pi-code status-icon mr-1 status-icon-clickable"
          :style="{ color: getVersionIconColor() }"
          @click.stop="openVersionDialog"
        ></i>
        <span class="status-label">Version:</span>
        <span class="status-value">
          {{ currentVersion }}
          <Tag
            v-if="versionMismatch"
            :value="`New: ${deployedVersion}`"
            :severity="versionMismatch ? 'danger' : 'secondary'"
            class="ml-1"
          />
        </span>
      </div>
    </div>
    <div v-else class="status-content offline-content">
      <i class="pi pi-wifi-off offline-icon"></i>
      <span class="offline-text">Offline</span>
    </div>
  </div>

  <!-- Settings Dialog -->
  <Dialog
    v-model:visible="showSettingsDialog"
    header="Device Settings"
    :modal="true"
    :style="{ width: '80vw', maxWidth: '800px' }"
    :maximizable="true"
  >
    <DeviceSettings />
  </Dialog>

  <!-- Version Dialog -->
  <Dialog
    v-model:visible="showVersionDialog"
    header="About ScrapBoss"
    :modal="true"
    :style="{ width: '90vw', maxWidth: '800px' }"
    :closable="true"
  >
    <div class="version-dialog">
      <div class="version-info">
        <p><strong>Version:</strong> {{ appVersion }}</p>
        <p><strong>Git Ref:</strong> {{ gitRef }}</p>
        <p><strong>Branch:</strong> {{ gitBranch }}</p>
        <p><strong>Build Date:</strong> {{ gitDate }}</p>
        <p><strong>Commit message:</strong> "{{ gitMessage }}"</p>
      </div>
    </div>
  </Dialog>

  <!-- Device Details Dialog -->
  <Dialog
    v-model:visible="showDeviceDetailsDialog"
    :header="deviceDetailsHeader"
    :modal="true"
    :style="{ width: '90vw', maxWidth: '600px' }"
    :closable="true"
  >
    <div v-if="selectedDeviceType && selectedDeviceId" class="device-details">
      <div class="grid">
        <div class="col-12">
          <div class="field">
            <label class="font-semibold">Name</label>
            <div>{{ selectedDevice?.name || 'N/A' }}</div>
          </div>
        </div>
        <div class="col-12">
          <div class="field">
            <label class="font-semibold">Status</label>
            <div>
              <Tag :value="selectedDeviceStatus" :severity="selectedDeviceStatusSeverity" />
            </div>
          </div>
        </div>
        <div v-if="selectedDevice?.location" class="col-12">
          <div class="field">
            <label class="font-semibold">Location</label>
            <div>{{ selectedDevice.location }}</div>
          </div>
        </div>
        <div
          v-if="selectedDevice && 'driver' in selectedDevice && (selectedDevice as any).driver"
          class="col-12"
        >
          <div class="field">
            <label class="font-semibold">Driver</label>
            <div>{{ (selectedDevice as any).driver }}</div>
          </div>
        </div>
        <div v-if="selectedDeviceAgent" class="col-12">
          <div class="field">
            <label class="font-semibold">Agent</label>
            <div>{{ selectedDeviceAgent.id }}</div>
          </div>
        </div>
        <div v-if="selectedDevice?.ops && selectedDevice.ops.length > 0" class="col-12">
          <div class="field">
            <label class="font-semibold">Operations</label>
            <div>
              <Tag
                v-for="op in selectedDevice.ops"
                :key="op"
                :value="op"
                severity="info"
                class="mr-1"
              />
            </div>
          </div>
        </div>
        <div
          v-if="
            selectedDevice &&
            'modes' in selectedDevice &&
            (selectedDevice as any).modes &&
            (selectedDevice as any).modes.length > 0
          "
          class="col-12"
        >
          <div class="field">
            <label class="font-semibold">Modes</label>
            <div>
              <Tag
                v-for="mode in (selectedDevice as any).modes"
                :key="mode"
                :value="mode"
                severity="secondary"
                class="mr-1"
              />
            </div>
          </div>
        </div>
        <div
          v-if="selectedDeviceType === 'scale' && (selectedDevice as any)?.assignedCameraId"
          class="col-12"
        >
          <div class="field">
            <label class="font-semibold">Assigned Camera</label>
            <div>{{ getCameraName((selectedDevice as any).assignedCameraId) }}</div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import { useDevicesStore } from '@/stores/v2/devices'
import DeviceSettings from '@/views/settings/DeviceSettings.vue'
import { checkVersion } from '@/services/versionService'
import { appVersion, gitRef, gitBranch, gitDate, gitMessage } from '@/utils/version'
import type { VersionCheckResult } from '@/services/versionService'
import { usePresence } from '@/composables/usePresence'

const { isOnline } = usePresence()
const devicesStore = useDevicesStore()
const showSettingsDialog = ref(false)
const showDeviceDetailsDialog = ref(false)
const showVersionDialog = ref(false)
const selectedDeviceType = ref<'scale' | 'camera' | 'scanner' | null>(null)
const selectedDeviceId = ref<string | null>(null)

// Device computed properties
const defaultScale = computed(() => devicesStore.defaultScale)
const defaultCamera = computed(() => devicesStore.defaultCamera)
const defaultScanner = computed(() => devicesStore.defaultScanner)
const agents = computed(() => devicesStore.agents)

// Device details header
const deviceDetailsHeader = computed(() => {
  if (!selectedDeviceType.value || !selectedDeviceId.value) return 'Device Details'

  let device
  if (selectedDeviceType.value === 'scale') {
    device = devicesStore.scales.find((s) => s.id === selectedDeviceId.value)
  } else if (selectedDeviceType.value === 'camera') {
    device = devicesStore.cameras.find((c) => c.id === selectedDeviceId.value)
  } else if (selectedDeviceType.value === 'scanner') {
    device = devicesStore.scanners.find((s) => s.id === selectedDeviceId.value)
  }

  return device ? `${device.name} - Details` : 'Device Details'
})

// Selected device
const selectedDevice = computed(() => {
  if (!selectedDeviceType.value || !selectedDeviceId.value) return null

  if (selectedDeviceType.value === 'scale') {
    return devicesStore.scales.find((s) => s.id === selectedDeviceId.value) || null
  } else if (selectedDeviceType.value === 'camera') {
    return devicesStore.cameras.find((c) => c.id === selectedDeviceId.value) || null
  } else if (selectedDeviceType.value === 'scanner') {
    return devicesStore.scanners.find((s) => s.id === selectedDeviceId.value) || null
  }

  return null
})

// Selected device agent
const selectedDeviceAgent = computed(() => {
  if (!selectedDeviceId.value) return null
  return getAgentForDevice(selectedDeviceId.value)
})

// Selected device status
const selectedDeviceStatus = computed(() => {
  if (!selectedDeviceId.value) return 'offline'
  return getDeviceStatus(selectedDeviceId.value)
})

const selectedDeviceStatusSeverity = computed(() => {
  return getDeviceStatusSeverity(selectedDeviceId.value || '')
})

// Helper to get camera name
function getCameraName(cameraId: string): string {
  const camera = devicesStore.cameras.find((c) => c.id === cameraId)
  return camera?.name || cameraId
}

// Version check state
const versionCheck = ref<VersionCheckResult | null>(null)
const currentVersion = computed(() => gitRef)
const deployedVersion = computed(() => versionCheck.value?.deployedVersion || null)
const versionMismatch = computed(() => versionCheck.value?.matches === false)

// Polling interval for version checks
let versionCheckInterval: ReturnType<typeof setInterval> | null = null

// Get agent for a device to check its online/offline status
function getAgentForDevice(deviceId: string) {
  for (const agent of agents.value) {
    const found =
      ((agent.devices?.scales as any[]) || []).some((x) => x.id === deviceId) ||
      ((agent.devices?.cameras as any[]) || []).some((x) => x.id === deviceId) ||
      ((agent.devices?.scanners as any[]) || []).some((x) => x.id === deviceId)
    if (found) return agent
  }
  return null
}

// Get device status (online/offline)
function getDeviceStatus(deviceId: string): string {
  const agent = getAgentForDevice(deviceId)
  return agent?.state || 'offline'
}

// Get device status severity for Tag component
function getDeviceStatusSeverity(deviceId: string) {
  const status = getDeviceStatus(deviceId)
  switch (status) {
    case 'online':
      return 'success'
    case 'offline':
      return 'danger'
    default:
      return 'secondary'
  }
}

// Get icon color based on device online status
function getDeviceIconColor(deviceId?: string): string {
  if (!deviceId) return '#ef4444' // red for offline
  const status = getDeviceStatus(deviceId)
  return status === 'online' ? '#22c55e' : '#ef4444' // green for online, red for offline
}

// Get icon color for version (green if up to date, red if mismatch)
function getVersionIconColor(): string {
  return versionMismatch.value ? '#ef4444' : '#22c55e' // red if mismatch, green if up to date
}

// Click handlers
function openSettingsDialog() {
  showSettingsDialog.value = true
}

function openDeviceDetails(deviceType: 'scale' | 'camera' | 'scanner', deviceId?: string) {
  if (!deviceId) return
  selectedDeviceType.value = deviceType
  selectedDeviceId.value = deviceId
  showDeviceDetailsDialog.value = true
}

function openVersionDialog() {
  showVersionDialog.value = true
}

// Check version function
async function performVersionCheck() {
  try {
    versionCheck.value = await checkVersion()
  } catch (error) {
    console.error('Failed to check version:', error)
  }
}

// Check version on mount and set up polling
onMounted(async () => {
  // Initial check
  await performVersionCheck()

  // Poll every 15 seconds for version updates
  versionCheckInterval = setInterval(async () => {
    await performVersionCheck()
  }, 15000) // 15 seconds
})

// Clean up interval on unmount
onUnmounted(() => {
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval)
    versionCheckInterval = null
  }
})
</script>

<style scoped>
.status-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--surface-50);
  border-top: 1px solid var(--surface-200);
  padding: 0.25rem 1rem;
  z-index: 1000;
  font-size: 0.75rem;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.status-bar-offline {
  border-top: 2px solid #dc2626;
}

.offline-content {
  color: #dc2626;
}

.offline-icon {
  font-size: 1rem;
  margin-right: 0.5rem;
}

.offline-text {
  font-weight: 600;
  font-size: 0.875rem;
}

.status-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-label {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.status-value {
  font-weight: 600;
  color: var(--text-color);
}

.clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.clickable:hover {
  opacity: 0.8;
}

.status-divider {
  width: 1px;
  height: 1rem;
  background: var(--surface-300);
}

.status-icon {
  font-size: 1rem;
  transition: color 0.2s;
}

.status-icon-clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.status-icon-clickable:hover {
  opacity: 0.7;
}

.status-item-clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.status-item-clickable:hover {
  opacity: 0.8;
}

.device-details {
  padding: 0.5rem 0;
}

.device-details .field {
  margin-bottom: 1rem;
}

.device-details .field label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-color-secondary);
}

.version-dialog {
  padding: 0.5rem 0;
}

.version-info {
  margin-bottom: 1.5rem;
}

.version-info p {
  margin: 0.5rem 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .status-content {
    flex-direction: row;
    gap: 1.5rem;
    justify-content: center;
  }

  .status-divider {
    display: none;
  }

  .status-bar {
    padding: 0.5rem 1rem;
  }

  .status-label,
  .status-value {
    display: none;
  }

  .status-icon {
    font-size: 1.25rem;
  }

  .status-item {
    gap: 0;
  }
}

/* Ensure content doesn't get hidden behind status bar */
:global(.layout-content) {
  padding-bottom: 3rem;
}

@media (max-width: 768px) {
  :global(.layout-content) {
    padding-bottom: 4rem;
  }
}
</style>
